'use client'

import React from 'react'

import { useShipplayAuth } from '@/hooks/useShipplayAuth'
import { brand } from '@/utils/brand'

import { ConciergeTeam } from './ConciergeTeam'
import { SiteFooter } from './SiteFooter'
import { SiteNav } from './SiteNav'
import { SiteUserBar } from './SiteUserBar'

interface SiteLayoutProps {
  children: React.ReactNode
}

/**
 * Kettle Ship&Play page shell — shared across shipsticks, shipskis, and shipplay.
 *
 * Composes the new nav / user bar / footer / brand family around page content.
 * Per-brand copy (brand name, concierge phone, footer email/phone/address,
 * brand-family headline) comes from brand.layoutContent.siteChrome.
 *
 * Each opt-in page wraps its content in this component; the wrapper bypasses
 * the legacy per-page SiteHeaderWrapper + scroll-collapse brand bar entirely.
 */
export const SiteLayout = ({ children }: SiteLayoutProps): React.ReactElement => {
  const auth = useShipplayAuth()
  const { siteChrome } = brand.layoutContent

  return (
    <>
      <SiteUserBar
        isLoggedIn={auth.isLoggedIn}
        firstName={auth.firstName}
        onLogIn={auth.onLogIn}
        onLogOut={auth.onLogOut}
      />
      <SiteNav
        logoUrl={siteChrome.logoUrl.dark}
        brandName={siteChrome.brandName}
        isLoggedIn={auth.isLoggedIn}
        onLogIn={auth.onLogIn}
        onLogOut={auth.onLogOut}
        onSignUp={auth.onSignUp}
      />
      {children}
      <ConciergeTeam content={siteChrome.conciergeTeam} />
      <SiteFooter
        brandName={siteChrome.brandName}
        logoUrl={siteChrome.logoUrl.light}
        email={siteChrome.footer.email}
        phone={siteChrome.footer.phone}
        address={siteChrome.footer.address}
        isLoggedIn={auth.isLoggedIn}
        onLogIn={auth.onLogIn}
        onLogOut={auth.onLogOut}
        onSignUp={auth.onSignUp}
      />
    </>
  )
}
